import { SendOutlined } from "@ant-design/icons";
import type { FileUIPart, UIMessage } from "ai";
import { Streamdown, type Components } from "streamdown";

import {
  Attachment,
  AttachmentInfo,
  AttachmentPreview,
  AttachmentRemove,
  Attachments,
} from "@fitness-app/web-ui/components/ai-elements/attachments";
import { Loader } from "@fitness-app/web-ui/components/ai-elements/loader";
import { MessageResponse } from "@fitness-app/web-ui/components/ai-elements/message";
import {
  PromptInputHeader,
  PromptInputSubmit,
  type PromptInputFile,
  usePromptInputAttachments,
} from "@fitness-app/web-ui/components/ai-elements/prompt-input";
import { Reasoning, ReasoningContent, ReasoningTrigger } from "@fitness-app/web-ui/components/ai-elements/reasoning";

import { getMessageFiles } from "./messageUtils";

export const SalesAgentAttachments = ({
  attachments,
  onRemove,
  variant = "grid",
}: {
  attachments: (FileUIPart & { id: string })[];
  onRemove?: (id: string) => void;
  variant?: "compact" | "grid" | "inline" | "list";
}) => {
  if (!attachments.length) return null;

  return (
    <Attachments variant={variant} className={variant === "grid" ? "mb-3 max-w-xl" : undefined}>
      {attachments.map((attachment) => (
        <Attachment
          key={attachment.id}
          data={attachment}
          onRemove={onRemove ? () => onRemove(attachment.id) : undefined}
        >
          <AttachmentPreview />
          <AttachmentInfo showMediaType={variant === "list"} />
          <AttachmentRemove />
        </Attachment>
      ))}
    </Attachments>
  );
};

const SalesAgentPromptAttachments = () => {
  const attachments = usePromptInputAttachments();

  if (attachments.files.length === 0) return null;

  return (
    <SalesAgentAttachments
      attachments={attachments.files as (PromptInputFile & { id: string })[]}
      onRemove={attachments.remove}
      variant="compact"
    />
  );
};

export const SalesAgentPromptHeader = () => {
  const attachments = usePromptInputAttachments();

  if (attachments.files.length === 0) return null;

  return (
    <PromptInputHeader>
      <SalesAgentPromptAttachments />
    </PromptInputHeader>
  );
};

export const SalesAgentPromptSubmit = ({
  input,
  status,
}: {
  input: string;
  status: "ready" | "submitted" | "streaming" | "error";
}) => {
  const attachments = usePromptInputAttachments();

  return (
    <PromptInputSubmit status={status} disabled={!input.trim() && attachments.files.length === 0}>
      <SendOutlined />
      <span className="sr-only">Wyślij</span>
    </PromptInputSubmit>
  );
};

export const SalesAgentMessageParts = ({
  hideLoader = false,
  isLastMessage,
  isStreaming,
  markdownComponents,
  message,
}: {
  /** Rodzic pokazuje własny wskaźnik pracy (asystent trenera) - bez drugiego loadera. */
  hideLoader?: boolean;
  isLastMessage: boolean;
  isStreaming: boolean;
  /** Własne renderery elementów markdownu (np. linki do profilu jako chip). */
  markdownComponents?: Components;
  message: UIMessage;
}) => {
  const parts = message.parts ?? [];
  const reasoningParts = parts.filter((part) => part.type === "reasoning");
  const reasoningText = reasoningParts
    .map((part) => part.text)
    .join("\n\n")
    .trim();
  const textParts = parts.filter((part) => part.type === "text");
  const fileParts = getMessageFiles(message);
  const lastPart = parts.at(-1);
  const isReasoningStreaming = isLastMessage && isStreaming && lastPart?.type === "reasoning";
  const hasVisibleContent = Boolean(reasoningText || fileParts.length || textParts.some((part) => part.text.trim()));

  return (
    <>
      {reasoningText ? (
        <Reasoning className="w-full" isStreaming={isReasoningStreaming}>
          <ReasoningTrigger />
          <ReasoningContent>{reasoningText}</ReasoningContent>
        </Reasoning>
      ) : null}
      <SalesAgentAttachments attachments={fileParts} variant={message.role === "user" ? "compact" : "grid"} />
      {textParts.map((part, index) => (
        <MessageResponse key={`${message.id}-${index}`}>
          <Streamdown
            controls={false}
            mode={isLastMessage && isStreaming ? "streaming" : "static"}
            parseIncompleteMarkdown
            components={markdownComponents}
          >
            {part.text}
          </Streamdown>
        </MessageResponse>
      ))}
      {isLastMessage && isStreaming && !hasVisibleContent && !hideLoader ? <Loader /> : null}
    </>
  );
};
